import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { ContentPlanRootState } from '../../elements/contentPlan/store';
import { ElementAction } from '../../elements/elementAction';
import { Metadata, Schema, SchemaField } from '../../types/Metadata';
import { Functions } from '../../types/types';
import {
  updateApprovalState,
  updateFieldErrors,
  updateMetadataFormData,
  updateMetadataFormState,
  updateMetadataFormStateField,
  updateShouldValidate,
  updateAllErrorFields
} from './metadataFormReducer';
import { FieldType } from '../../enums/FieldType';
import {
  addIsImpliedValue,
  clone,
  fieldIsRequired,
  fieldIsRequiredNext,
  getItemStatusId,
  getMetadata,
  getSchema,
  getInvalidItemDataReference,
  isRequiredFieldValueEmpty,
  isNullOrEmpty,
  parseDate,
  getFieldValue,
  getSchemaForm,
  getItemType,
  canActionTask
} from '../../utils';
import { deepEquals } from 'rcd-cpp-workspace/utils/deepEquals';
import { getDataValueByField } from '../../utils/fieldUtils';
import { WebSocketManager } from '../../services/WebSocketManager';
import { NodeType } from '../../enums/NodeType';
import { Op } from '../../enums/Op';
import { PlanningState } from '../ContentPlanning/contentPlanningReducer';
import { DataType } from '../../enums/DataType';
import { setUserSetting } from '../ContentPlanning/contentPlanningActions';
import { AppRefId } from '../../utils/constants';
import { TreeFieldUtils } from '../../utils/treeFieldUtils';
import { toTreeNode, traverse } from '../TreeSelect/treeSelect';
import {
  flattenImpliedNodes,
  shouldFlattenImpliedNodes
} from '../../utils/treeUtils';
import { ValidationResponse } from '../../types/ValidationResponse';
import { ViewType } from '../../enums/ViewType';
import { getSearchPayload, loadData } from '../View/viewActions';
import { findOne, flatten } from 'rcd-cpp-workspace/utils/array';
import { API } from '../../api';
import { updateViewState, ViewState } from '../View/viewReducer';
import { MetadataProps, View, WorkspaceSetting } from '../../types';
import { ToasterWithLinkWrapper, useToaster } from '../Toaster/toaster';
import URLManager from '../../utils/urlManager';
import { FullStoryManager } from '../App/fullStory';
import { TriggerLocation } from '../../enums/TriggerLocation';
import { ItemStatus } from '../../enums/ItemStatus';
import { ApprovalTask } from '../../enums/ApprovalTask';
import { FSEvent } from '../../enums/FSEvent';
import { InvalidWorkspaceError } from '../Alert/invalidWorkspaceError';
import { ApprovalType } from '../../enums/ApprovalType';
import { RetirementProps } from '../../types/RetirementProps';
import { ErrorName } from '../../enums/ErrorName';
import { ItemType } from '../../enums/ItemType';
import {
  ConferenceItemLanguageId,
  ContentItemLanguageId
} from '../../enums/ItemLanguage';
import { ViewCategory } from '../../enums/viewCategory';
import { ValidationMode } from '../../enums/ValidationMode';
import { FieldData } from '../../types/Field';
import { ShowFieldsOption } from '../../enums/ShowFieldsOption';

type S = ContentPlanRootState;

const showForm =
  (
    data: string | Partial<Metadata>,
    schema?: Schema,
    commentId?: string,
    metrics?: string
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { view, planning } = getState();
    const { statusCategories } = planning;
    let fsData: Metadata = null;
    const { getDataDetail, searchReferences } = getAPI();
    if (typeof data == 'string') {
      const schemas = planning.selectedWorkspace?.schemas;
      if (schemas) {
        const r = await getDataDetail(data);
        const d = r?.data[0];
        const schema = getSchema(schemas, d?.labels);
        fsData = d;
        if (d && schema) {
          dispatch(
            updateMetadataFormState({
              data: d,
              original: JSON.parse(JSON.stringify(d)),
              schema,
              comments: null,
              autoAccepted: false,
              shouldValidate: ValidationMode.None,
              pushResponse: null,
              fieldErrors: null,
              shouldShowComments: !!commentId,
              shouldShowMetrics: !!metrics
            })
          );
          dispatch(loadComments(data));
        }
      }
    } else {
      const s = schema || view.selected.dataSchema;
      if (data?.props?.refId) {
        const r = await getDataDetail(data.props.refId, s?.props?.type);
        const d = r?.data[0];
        fsData = d;
        dispatch(
          updateMetadataFormState({
            data: d,
            original: JSON.parse(JSON.stringify(d)),
            schema: s,
            comments: null,
            autoAccepted: false,
            shouldValidate: ValidationMode.None,
            pushResponse: null
          })
        );
        dispatch(loadComments(data?.props?.refId));
      } else {
        data.labels = [s.props.type];
        if (!data.props) data.props = {};
        if (s.props.type == NodeType.Item) {
          if (!data.props.status) {
            data.props.status = [
              {
                props: {
                  refId: 'status:1',
                  id: ItemStatus.Idea,
                  description: 'Idea'
                }
              }
            ];
          }
          if (!data.props.language?.length) {
            data.props.language = [
              {
                props: {
                  refId: 'lang:en',
                  description: 'English',
                  contentId: ContentItemLanguageId.English,
                  conferenceId: ConferenceItemLanguageId.English
                }
              }
            ];
          }
        }
        await populateDefaultFieldData(s, data, { searchReferences });
        const activeField = findOne(s.props.fields, { field: 'isActive' });
        if (activeField && !s.props.isManaged) data.props.isActive = true;
        dispatch(
          updateMetadataFormState({
            data,
            schema: s,
            comments: null,
            shouldValidate: ValidationMode.None,
            pushResponse: null
          })
        );
      }
    }
    if (fsData?.labels?.includes(NodeType.Item)) {
      const statusId = fsData?.props?.status?.length
        ? fsData?.props?.status[0]?.props?.id
        : null;
      const workflowPhase = statusCategories
        ? statusCategories[statusId]?.props?.name
        : null;
      dispatch(
        FullStoryManager.getInstance().captureEvent(FSEvent.OpenItemDetails, {
          workflowPhase
        })
      );
    }
  };

const populateDefaultFieldData = async (
  schema: Schema,
  data: Partial<Metadata>,
  api: Pick<API, 'searchReferences'>
) => {
  const nodeTypes = [];
  const refId2Metadata = {};
  schema?.props?.fields?.forEach(field => {
    if (field.defaultValues?.length && !data?.props[field.field]) {
      if (
        field.fieldType == FieldType.reference &&
        !!field.dataType &&
        !nodeTypes.includes(field.dataType)
      ) {
        nodeTypes.push(field.dataType);
      }
      if (field.fieldType == FieldType.reference)
        field.defaultValues.forEach(v => {
          {
            if (typeof v == 'string') {
              refId2Metadata[v] = null;
            }
          }
        });
    }
  });
  const ids = Object.keys(refId2Metadata);
  if (ids.length > 0 && nodeTypes.length > 0) {
    const nodeType = nodeTypes.join('|') as NodeType;
    const results = await api.searchReferences({ nodeType, ids });
    results.forEach(m => {
      refId2Metadata[m.props.refId] = m;
    });
  }
  schema?.props?.fields?.forEach(field => {
    if (field.defaultValues?.length && !data?.props[field.field]) {
      if (field.fieldType == FieldType.reference) {
        data.props[field.field] = field.defaultValues
          .map(v => {
            if (typeof v == 'string') {
              return refId2Metadata[v];
            } else {
              return v;
            }
          })
          .filter(x => x);
      } else {
        if (field.multiValue) {
          data.props[field.field] = field.defaultValues;
        } else {
          data.props[field.field] = field.defaultValues[0];
        }
      }
    }
  });
};

const closeForm = (): ElementAction<Functions, S> => (dispatch, getState) => {
  dispatch(
    updateMetadataFormState({ schema: null, data: null, comments: null })
  );
};

const slideOut = (): ElementAction<Functions, S> => (dispatch, getState) => {
  if (getState().form?.data?.id) {
    dispatch(updateMetadataFormState({ data: null, comments: null }));
  }
};

const create =
  (
    toaster?: ToasterWithLinkWrapper,
    addAdditional?: boolean,
    createInContent?: boolean
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    dispatch(updateShouldValidate({ shouldValidate: ValidationMode.Current }));
    const { createData } = getAPI();
    const { form, planning } = getState();
    const {
      triggerLocation,
      parentStatusId,
      parentStatusText,
      readonlyFields,
      ...data
    } = form.data;
    if (!data.id) {
      const schema = getSchema(planning.selectedWorkspace.schemas, data.labels);
      setDefaultValueForRequiredBooleans(schema, data);
      if (getEmptyRequiredFields(schema, data).length > 0) {
        return;
      }
      try {
        toaster?.show('Creating item...');
        data.createInContent = createInContent;
        const newData = await createData({ data });
        schema.lastDataModifiedTime = new Date();
        dispatch(reloadDataInView(newData?.props?.refId));

        let m = {
          props: {}
        } as Metadata;

        if (!!data?.props?.itemType) {
          m.props.itemType = data?.props?.itemType;
        }

        addAdditional ? dispatch(showForm(m, schema)) : dispatch(closeForm());
        if (toaster) {
          const messages = await getToasterMessages(
            newData,
            getState(),
            getAPI()
          );
          toaster.success({ messages });
        }
      } catch (e) {
        console.error('Failed to create item with error ', e);
        if (toaster) {
          if (e.message) {
            toaster.error({
              header: 'Failed to create item...',
              messages: [e.message]
            });
          } else {
            toaster.error('Failed to create item...');
          }
        }
        if (
          [
            ErrorName.FieldValidationError,
            ErrorName.DuplicateFieldError
          ].includes(e.name) &&
          e.additionalInfo
        ) {
          if (getState().form?.data?.props?.refId == null) {
            dispatch(updateFieldErrors(e.additionalInfo));
          }
        }
        if (!toaster) {
          throw e;
        }
      }
    }
  };

const getToasterMessages = async (data: Metadata, state: S, api: API) => {
  try {
    const newItemRefId = data?.props?.refId;
    const { searchData } = api;
    const { view, planning } = state;
    const selView = view.selected;
    const schema = getSchema(planning.selectedWorkspace.schemas, data.labels);
    const displayFieldKey = schema.props.type + '.' + schema.props.displayField;

    let message = ` has been created.`;
    if (selView && selView.category != ViewCategory.Administration) {
      const doesItemExistInView = await searchViewForItem(
        searchData,
        view,
        selView,
        planning,
        {
          field: displayFieldKey,
          conditions: [{ [Op.refIds]: [newItemRefId] }]
        }
      );
      if (!doesItemExistInView?.data?.length) {
        const doesItemExistInDefaultView = await searchViewForItem(
          searchData,
          view,
          selView,
          planning,
          {
            field: displayFieldKey,
            conditions: [{ [Op.refIds]: [newItemRefId] }]
          },
          true
        );

        if (doesItemExistInDefaultView?.data?.length) {
          message = ` has been created but you need to clear filters to see it on the current view.`;
        } else {
          message = ` has been created but is not visible on the current view.`;
        }
      }
    }
    return [
      {
        url: URLManager.generateUrl(planning.selectedWorkspace, {
          v: view.selected,
          id: data.props.refId
        }),
        params: { id: data.props.refId },
        text: data.props[schema?.props?.displayField] || '<Title Not Defined>'
      },
      message
    ];
  } catch (e) {
    throw e;
  }
};

const searchViewForItem = async (
  searchData: Function,
  view: ViewState,
  selView: View.Setting,
  planning: PlanningState,
  additionalFilters: View.FiltersField,
  useDefaultFiltersOnly?: boolean
) => {
  const clonedView = clone(view);
  if (useDefaultFiltersOnly) {
    clonedView.filters = [];
  }
  const { payload: searchViewPayload } = getSearchPayload(
    clonedView,
    selView,
    planning
  );
  const viewPayloadWithItem = clone(searchViewPayload);
  if (!viewPayloadWithItem.filters) {
    viewPayloadWithItem.filters = [];
  }
  viewPayloadWithItem.filters.push(additionalFilters);
  const results = await searchData(viewPayloadWithItem);
  return results;
};

const createComment =
  (
    message: string,
    mentions: any[],
    parentCommentRefId: string
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { createComment } = getAPI();
    const { form, app, planning, view } = getState();
    const data = form.data;
    const comments = form.comments;
    const user = app?.user;
    const selectedWorkspace = planning?.selectedWorkspace;
    const statusCategories = planning?.statusCategories;
    try {
      if (!data?.props?.refId) {
        throw new Error(`Only comments within Plannning Items are supported. `);
      }
      const viewRefId = view?.selected?.props?.refId;
      const workspaceRefId = selectedWorkspace?.props?.refId;
      const username = user.firstName + ' ' + user.lastName;
      if (parentCommentRefId) {
        const parent = comments.filter(c => c.refId == parentCommentRefId)[0];
        parent.childComments.push({
          message,
          srcRefId: data.props.refId,
          isResolved: false,
          createdByTime: new Date(),
          modifiedByTime: new Date(),
          createdByEmpName: username,
          createdByEmpCd: user.employeeId,
          modifiedByEmpName: username,
          modifiedByEmpCd: user.employeeId
        });
      } else {
        comments.unshift({
          message,
          srcRefId: data.props.refId,
          isResolved: false,
          createdByTime: new Date(),
          modifiedByTime: new Date(),
          createdByEmpName: username,
          createdByEmpCd: user.employeeId,
          modifiedByEmpName: username,
          modifiedByEmpCd: user.employeeId
        });
      }
      dispatch(
        updateMetadataFormStateField({
          comments
        })
      );
      if (data?.labels?.includes(NodeType.Item)) {
        const statusId = data?.props?.status?.length
          ? data?.props?.status[0]?.props?.id
          : null;
        const workflowPhase = statusCategories
          ? statusCategories[statusId]?.props?.name
          : null;
        dispatch(
          FullStoryManager.getInstance().captureEvent(FSEvent.CreateComment, {
            workflowPhase
          })
        );
      }
      const defaultLabels = data.labels || [NodeType.Item];
      const commentPayload: any = {
        message,
        mentions,
        srcRefId: data.props.refId,
        viewRefId,
        parentCommentRefId,
        isResolved: false,
        labels: defaultLabels,
        workspaceRefId: workspaceRefId || null
      };
      await createComment(commentPayload);
    } catch (e) {
      console.error(`Failed to post comment with error`, e);
    }
    dispatch(reloadDataInView());
    dispatch(loadComments(data.props.refId));
  };

const updateComment =
  (refId: string, props: any): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { updateComment } = getAPI();
    const { form, planning } = getState();
    const { statusCategories } = planning;
    const data = form.data;
    const comments = form.comments;
    try {
      const comment = flatten(comments, c => c.childComments)
        .filter(c => c)
        .filter(c => c.refId == refId)[0];

      if (props?.message?.length) {
        comment.message = props.message;
      }
      if (props?.isResolved === false || props?.isResolved === true) {
        comment.isResolved = props.isResolved;
      }

      dispatch(
        updateMetadataFormStateField({
          comments
        })
      );

      if (data?.labels?.includes(NodeType.Item)) {
        const statusId = data?.props?.status?.length
          ? data?.props?.status[0]?.props?.id
          : null;
        const workflowPhase = statusCategories
          ? statusCategories[statusId]?.props?.name
          : null;
        dispatch(
          FullStoryManager.getInstance().captureEvent(FSEvent.UpdateComment, {
            workflowPhase
          })
        );
      }
      const updatedComment = await updateComment({
        refId,
        props
      });
    } catch (e) {
      console.error(`Failed to update comment with error`, e);
    }
    dispatch(reloadDataInView(refId));
    dispatch(loadComments(data.props.refId));
  };

const deleteComment =
  (refId: string): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { deleteComment } = getAPI();
    const { form, planning } = getState();
    const { statusCategories } = planning;
    const data = form.data;
    let comments = form.comments;
    try {
      const comment = flatten(comments, c => c.childComments)
        .filter(c => c)
        .filter(c => c.refId == refId)[0];

      if (comment.parentCommentRefId?.length) {
        const parentComment = comments.filter(
          c => c.refId == comment.parentCommentRefId
        )[0];
        parentComment.childComments = parentComment.childComments.filter(
          cc => cc.refId != refId
        );
      } else {
        comments = comments.filter(c => c.refId != refId);
      }
      dispatch(reloadDataInView());
      dispatch(
        updateMetadataFormStateField({
          comments
        })
      );

      if (data?.labels?.includes(NodeType.Item)) {
        const statusId = data?.props?.status?.length
          ? data?.props?.status[0]?.props?.id
          : null;
        const workflowPhase = statusCategories
          ? statusCategories[statusId]?.props?.name
          : null;
        dispatch(
          FullStoryManager.getInstance().captureEvent(FSEvent.DeleteComment, {
            workflowPhase
          })
        );
      }
      const deletedComment = await deleteComment({
        refId
      });
    } catch (e) {
      console.error(`Failed to delete comment with error`, e);
    }
    dispatch(loadComments(data.props.refId));
  };

const loadComments =
  (refId: string): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { getComments } = getAPI();
    let comments = await getComments(refId);
    const sortDescFn = (c1, c2) => {
      return parseDate(c1.createdByTime).getTime() <
        parseDate(c2.createdByTime).getTime()
        ? 1
        : -1;
    };
    const sortAscFn = (c1, c2) => {
      return parseDate(c1.createdByTime).getTime() >
        parseDate(c2.createdByTime).getTime()
        ? 1
        : -1;
    };
    comments.sort(sortDescFn).forEach(c => {
      if (c.childComments?.length) {
        c.childComments.sort(sortAscFn);
      }
    });

    dispatch(
      updateMetadataFormStateField({
        comments
      })
    );
  };

const showHideCommentWidget =
  (): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { form } = getState();
    dispatch(
      updateMetadataFormStateField({
        shouldShowComments: !form.shouldShowComments
      })
    );
  };

const validate =
  (): ElementAction<Functions, S> => async (dispatch, getState) => {
    const { data } = getState().form;
    const itemStatusId = getItemStatusId(data);
    if (itemStatusId == ItemStatus.Concept_Review) {
      await dispatch(pushToCPP(true));
      return getState().form.allErrorFields;
    } else if (itemStatusId == ItemStatus.Idea) {
      dispatch(updateShouldValidate({ shouldValidate: ValidationMode.Next }));
    }
  };

const validateModalData =
  (modalData: Partial<Metadata>): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { planning, form } = getState();
    const { pushToCPP } = getAPI();
    const workspaceRefId = planning.selectedWorkspace.props.refId;
    let dataToPush = JSON.parse(JSON.stringify(modalData)) as Partial<Metadata>;

    const additionalFieldsToValidate = [
      'additionalFunctions',
      'additionalQuestions'
    ];
    dataToPush.props.itemType = form?.data?.props?.itemType;
    dataToPush.props.refId = form?.data?.props?.refId;
    dataToPush.labels.push(NodeType.ContentItem);

    const pushResponse = await pushToCPP({
      data: dataToPush,
      workspaceRefId,
      validation: null,
      validateOnly: true,
      additionalFieldsToValidate
    });

    return pushResponse;
  };

const runRules =
  (data: Partial<Metadata>): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { planning } = getState();
    const { runRules } = getAPI();
    const workspaceRefId = planning.selectedWorkspace.props.refId;

    let dataToPush = JSON.parse(JSON.stringify(data));

    const ruleResp = await runRules({
      data: dataToPush,
      workspaceRefId
    });
    const rulesResponse = ruleResp?.data?.find(
      d => d.props?.refId === data.props?.refId
    );

    return rulesResponse;
  };

const getValidateInProgressMsg = (
  validateOnly: boolean,
  data: Partial<Metadata>
) => {
  if (validateOnly) return 'Validating...';
  const itemType = getItemType(data);
  if (itemType == ItemType.globalSubmission)
    return 'Sending to CPP Conferences';
  return 'Sending to CPP Production';
};

const pushToCPP =
  (validateOnly?: boolean): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const validateInProgressMsg = getValidateInProgressMsg(
      validateOnly,
      getState().form?.data
    );
    try {
      dispatch(updateShouldValidate({ validateInProgressMsg }));
      const autoAcceptResults = await dispatch(autoAccept(validateOnly));
      const validationResults = autoAcceptResults?.validationResults;
      const impliedDataProps = autoAcceptResults?.impliedDataProps;

      const { pushToCPP, getDataDetail } = getAPI();
      const { form, planning } = getState();
      const { data, schema } = form;
      const schemaForm = getSchemaForm(schema, data, true);
      const workspaceRefId = planning.selectedWorkspace.props.refId;

      if (data?.props?.refId) {
        const r = await getDataDetail(data.props.refId, schema?.props?.type);
        const d = r?.data[0];
        dispatch(
          updateMetadataFormState({
            data: d,
            original: JSON.parse(JSON.stringify(d)),
            schema,
            shouldValidate: ValidationMode.None,
            pushResponse: null
          })
        );
      }

      let dataToPush = JSON.parse(JSON.stringify(data));

      if (validateOnly && !isNullOrEmpty(impliedDataProps)) {
        dataToPush = {
          ...(dataToPush || {}),
          props: {
            ...(dataToPush?.props || {}),
            ...impliedDataProps
          }
        };
      }
      const additionalFieldsToValidate = schemaForm?.props?.fields
        .map(f => {
          const isRequired = fieldIsRequired(f, dataToPush);
          const isRequiredNext = fieldIsRequiredNext(
            f,
            dataToPush,
            impliedDataProps
          );
          const isRequiredForPush = isRequired || isRequiredNext;
          return isRequiredForPush ? f.field : null;
        })
        ?.filter(f => f);
      dataToPush.props.status = {
        labels: ['Status'],
        props: {
          id: 12,
          refId: 'status:12'
        }
      };
      const pushResponse = await pushToCPP({
        data: dataToPush,
        workspaceRefId,
        validation: validationResults,
        validateOnly,
        additionalFieldsToValidate
      });
      if (!!pushResponse) {
        dispatch(
          updateMetadataFormStateField({
            pushResponse,
            autoAccepted: validateOnly ? false : true,
            shouldValidate:
              pushResponse?.errors?.length || pushResponse?.warnings?.length
                ? ValidationMode.Next
                : ValidationMode.None
          })
        );
        if (!pushResponse?.errors?.length && !pushResponse?.warnings?.length) {
          const fromStatus = data?.props?.status?.length
            ? data?.props?.status[0]
            : null;
          dispatch(
            FullStoryManager.getInstance().captureEvent(
              FSEvent.UpdateItemStatus,
              {
                fromStatusId: fromStatus?.props?.id,
                toStatusId: ItemStatus.Review_Done_Start_Production,
                fromStatusText: fromStatus?.props?.description,
                toStatusText: `Pending CPP Item Creation`
              }
            )
          );
          if (!data?.props?.inThePlan) {
            dispatch(
              FullStoryManager.getInstance().captureEvent(
                FSEvent.AddedOrRemovedFromThePlan,
                {
                  from: false,
                  to: true,
                  when: `Push To CPP`,
                  triggerLocation: TriggerLocation.SLIDE_OUT
                }
              )
            );
          }
        }
      }

      const reqNum = new Date().getTime();
      const formData = getState().form?.data;

      if (formData?.props?.refId) {
        const payload = createSearchPayload(planning, schema, formData, true);
        dispatch(updateMetadataFormState({ reqNum }));
        const { data: r } = await getDataDetail(
          formData.props.refId,
          schema?.props?.type
        );
        if (r?.length && r[0]) {
          dispatch(
            updateMetadataFormState({
              reqNum,
              data: r[0],
              original: JSON.parse(JSON.stringify(r[0]))
            })
          );
        }
      }
      dispatch(reloadDataInView());
    } catch (e) {
      console.error(
        `An unexpected error occurred while creating a CPP Item`,
        e
      );
      throw e;
    } finally {
      dispatch(updateShouldValidate({ validateInProgressMsg: null }));
    }
  };

const autoAccept =
  (validateOnly?: boolean): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { form, planning } = getState();
    const { data, schema, autoAccepted } = form;

    if (autoAccepted) return;
    if (schema?.props?.type != NodeType.Item)
      throw new Error('Auto accept only for Item Schema');
    if (!data?.labels?.includes(NodeType.Item))
      throw new Error('Auto accept only for Item Data');
    if (!data.metadata?.length) return; // No Implied data
    if (!data.props) data.props = {};

    const schemaForm = getSchemaForm(schema, data);
    const cppRefFields = schemaForm.props.fields.reduce<SchemaField[]>(
      (fields, f) => {
        if (
          f.fieldType == FieldType.reference &&
          f.isSystemField &&
          ![NodeType.Item, NodeType.Employee].includes(f.dataType as any)
        ) {
          if (f.relationProps?.type == 'P') fields.unshift(f);
          else fields.push(f);
        }
        return fields;
      },
      []
    );
    const r = await getAutoAcceptData(
      data,
      schema,
      cppRefFields,
      planning.selectedWorkspace?.setting,
      getAPI()
    );
    const fieldsToUpdate = r.fieldsToUpdate;
    if (fieldsToUpdate.length) {
      if (!validateOnly) {
        dispatch(updateFields(fieldsToUpdate, true));
        await dispatch(updateData(fieldsToUpdate));
      }
    }
    return r;
  };

const getAutoAcceptImpliedDataProps =
  (data: Partial<Metadata>, schema: Schema): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }): Promise<MetadataProps> => {
    const { planning } = getState();

    if (schema?.props?.type != NodeType.Item) return {};
    if (!schema?.props?.fields?.length) return {};
    if (!data?.labels?.includes(NodeType.Item)) return {};
    if (!data.metadata?.length) return {};
    if (!data.props) data.props = {};
    const selectedWorkspace = planning.selectedWorkspace;
    const itemSchema = getSchema(selectedWorkspace.schemas, NodeType.Item);

    const { impliedDataProps: contentTypeImplied } = await getAutoAcceptData(
      data,
      schema,
      [findOne(itemSchema.props.fields, { field: 'contentType' })],
      planning.selectedWorkspace?.setting,
      getAPI()
    );

    const refFields = schema.props.fields?.reduce<SchemaField[]>(
      (fields, f) => {
        if (
          !fieldIsRequiredNext(f, data, contentTypeImplied) &&
          !fieldIsRequired(f, data)
        ) {
          return fields;
        }
        if (
          f.fieldType == FieldType.reference &&
          f.relationType != 'ITEM_ITEM' &&
          ![NodeType.Item, NodeType.Employee].includes(f.dataType as any)
        ) {
          if (f.relationProps?.type == 'P') fields.unshift(f);
          else fields.push(f);
        }
        return fields;
      },
      []
    );
    const { impliedDataProps } = await getAutoAcceptData(
      data,
      schema,
      refFields,
      planning.selectedWorkspace?.setting,
      getAPI()
    );
    return { ...impliedDataProps, ...contentTypeImplied };
  };

const getAutoAcceptData = async (
  data: Partial<Metadata>,
  schema: Schema,
  fields: SchemaField[],
  workspaceSetting: WorkspaceSetting,
  api: API
) => {
  const { getTree } = api;
  const validationResults: ValidationResponse = { errors: [], warnings: [] };
  const validationMessage = 'Please accept one implied value.';

  const fieldsToUpdate: { field: SchemaField; value: any }[] = [];
  const schemaForm = getSchemaForm(schema, data);
  for (const field of fields) {
    const treeSettings = TreeFieldUtils.getTreeSetting(schemaForm, field);
    const dataType = field.dataType as NodeType;
    if (!treeSettings) {
      const value = [...(data.props[field.field] || [])];
      const beginSize = value.length;
      const impliedData = getMetadata(data.metadata, dataType);
      if (!field.multiValue && !value.length && impliedData.length > 1) {
        validationResults.warnings.push({ key: field.key, validationMessage });
        continue;
      }
      for (const implied of impliedData) {
        if (!field.multiValue && value.length) continue;
        value.push(addIsImpliedValue(implied));
      }

      if (value.length > beginSize) {
        fieldsToUpdate.push({ field, value });
      }
    } else if (treeSettings.tree && treeSettings.settings?.length) {
      const { getFieldsToUpdate, getSelected, getImpliedData, addToList } =
        TreeFieldUtils;
      const filterImplied = TreeFieldUtils.filterImplied(
        workspaceSetting,
        treeSettings,
        data
      );
      const { settings } = treeSettings;
      const treeData = await getTree(treeSettings);
      const impliedData = getImpliedData({ field, data, settings });
      const impliedMap = impliedData.reduce((r, m) => {
        r[m.props.refId] = true;
        return r;
      }, {});
      let nodes = filterImplied(
        toTreeNode(treeData, impliedMap, null, {
          settings,
          ...TreeFieldUtils
        }).filter(n => n.isSelected || n.numOfDecendentsSelected > 0)
      );

      if (shouldFlattenImpliedNodes(settings)) {
        nodes = flattenImpliedNodes(nodes);
      }

      let selectedList = getSelected(settings, data);
      const beginSize = selectedList.length;

      if (!field.multiValue && !selectedList.length && nodes.length > 1) {
        validationResults.warnings.push({ key: field.key, validationMessage });
        continue;
      }

      for (const node of nodes) {
        if (!field.multiValue && selectedList.length > 0) continue;
        traverse([node], n => {
          n.data = addIsImpliedValue(n.data);
          n.parents.forEach(p => (p = addIsImpliedValue(p)));
          if (n.isLeaf && n.isSelected) {
            selectedList = addToList(n.data, n.parents, selectedList);
          } else if (n.isSelected && !n.numOfChildSelected) {
            selectedList = addToList(n.data, n.parents, selectedList);
          }
        });
      }

      const treeFields = getFieldsToUpdate(selectedList, settings, schema);
      if (selectedList.length > beginSize) {
        fieldsToUpdate.push(...treeFields);
      }
    }
  }

  const impliedDataProps = fieldsToUpdate.reduce((props, { field, value }) => {
    if (field?.field) props[field.field] = value;
    return props;
  }, {} as MetadataProps);

  const hasErrorOrWarning =
    validationResults.errors.length || validationResults.warnings.length;
  return {
    validationResults: !hasErrorOrWarning ? null : validationResults,
    fieldsToUpdate,
    impliedDataProps
  };
};

const deleteData =
  (refId: string, type: NodeType): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    if (refId && type) {
      const { deleteData } = getAPI();
      await deleteData({ refId, type });
      dispatch(reloadDataInView());
      dispatch(closeForm());
      const schema = getSchema(
        getState().planning?.selectedWorkspace?.schemas,
        type
      );
      if (schema) schema.lastDataModifiedTime = new Date();
    }
  };

const updateField =
  (field: SchemaField, value: any): ElementAction<Functions, S> =>
  async (dispatch, getState) => {
    await dispatch(updateFields([{ field, value }]));
  };

const updateFields =
  (fields: FieldData[], autoAccepted?: boolean): ElementAction<Functions, S> =>
  async (dispatch, getState) => {
    const { form, planning } = getState();
    const { data, schema } = form;
    const { selectedWorkspace } = planning;
    if (!data) return;
    if (!data.props) data.props = {};
    if (selectedWorkspace?.disableItemDataRefIds?.length) {
      const props = { ...data.props };
      const oldProps = {};
      fields.forEach(({ field, value }) => {
        props[field.field] = value;
        oldProps[field.field] = data.props[field.field];
      });
      const schemaForm = getSchemaForm(schema, data);
      const invalidDataRef = getInvalidItemDataReference(
        selectedWorkspace,
        schemaForm,
        { ...data, props }
      );
      if (!!invalidDataRef) {
        if (autoAccepted)
          dispatch(
            updateMetadataFormState({ data: { ...data }, autoAccepted })
          );
        else dispatch(updateMetadataFormState({ data: { ...data } }));
        throw new InvalidWorkspaceError('Failed to update item', {
          invalidDataRef,
          itemRefId: data.props.refId
        });
      }
    }
    fields.forEach(({ field, value }) => {
      data.props[field.field] = value;
    });

    dispatch(updateFieldErrors(null));

    if (autoAccepted)
      dispatch(updateMetadataFormState({ data: { ...data }, autoAccepted }));
    else dispatch(updateMetadataFormState({ data: { ...data } }));
  };

const updateData =
  (
    fields: SchemaField | FieldData[],
    value?: any
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    if (!Array.isArray(fields)) {
      await dispatch(updateData([{ field: fields, value }]));
    }
    const { form } = getState();
    const { data, original, schema } = form;
    if (data?.id && Array.isArray(fields)) {
      const props = {};
      for (const { field, value } of fields) {
        let dataValue = data.props[field.field];
        if (
          fieldIsRequired(field, data) &&
          field.dataType != DataType.boolean &&
          isRequiredFieldValueEmpty(dataValue)
        ) {
          const existingShouldValidate = form?.shouldValidate;
          dispatch(
            updateShouldValidate({
              shouldValidate:
                !!existingShouldValidate &&
                existingShouldValidate == ValidationMode.Next
                  ? ValidationMode.Next
                  : ValidationMode.Current
            })
          );
          console.error(
            `Failed to update ${field?.name}, cannot delete a required field.`
          );
          return;
        }
        let originalValue = original.props[field.field];

        if (field.fieldType == FieldType.reference) {
          dataValue = getDataValueByField(dataValue, field);
          originalValue = getDataValueByField(originalValue, field);
        }
        //to do: after deep equals fn is updated in module we can...
        //remove the second condition and add a 3rd param to deepEquals for strict type check
        //(after 3.7.0 release)
        if (
          deepEquals(dataValue, originalValue) &&
          !(dataValue === null && originalValue === 0)
        ) {
          continue;
        }
        if (
          field.fieldType == FieldType.reference ||
          field.fieldType == FieldType.static
        ) {
          props[field.field] = value;
        }
      }
      if (Object.keys(props).length > 0) {
        const { updateData, getDataDetail } = getAPI();
        if (typeof (props as any).inThePlan === 'boolean') {
          dispatch(
            FullStoryManager.getInstance().captureEvent(
              FSEvent.AddedOrRemovedFromThePlan,
              {
                from: !(props as any).inThePlan,
                to: (props as any).inThePlan,
                when: data?.props?.status[0]?.props?.description,
                triggerLocation: TriggerLocation.SLIDE_OUT
              }
            )
          );
        }
        dispatch(updateFieldErrors(null));
        await dispatch(
          updateDataRaw({
            labels: data.labels,
            props: {
              refId: data.props.refId,
              ...props
            }
          })
        );
      }
    }
  };

const updateConfigureWorkflow =
  (
    d: Partial<Metadata>,
    isLightUpdate: boolean,
    itemStatusId: number
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    if (!!d.props?.itemId) {
      const { updateConfigureWorkflow, updateLightUpdateConfigureWorkflow } =
        getAPI();
      if (isLightUpdate) {
        await updateLightUpdateConfigureWorkflow(d, itemStatusId);
      } else {
        await updateConfigureWorkflow(d, itemStatusId);
      }

      if (
        (itemStatusId == ItemStatus.WaitingforEditing &&
          d.props.configureWorkflowSkipIds?.includes(
            ItemStatus.InitialEditingIntake
          )) ||
        (itemStatusId == ItemStatus.WaitingforLUEditing &&
          d.props.configureLightUpdateWorkflowSkipIds?.includes(
            ItemStatus.LightUpdateEditorReview
          ))
      ) {
        const toasters = useToaster();
        toasters
          .createToaster()
          .show(
            'You have configured your item for Proofreading. It will enter Proofreading within the next 20 minutes.'
          );
      }
      const dataToUpdate = Object.keys(d.props)
        .filter(key => key !== 'refId' && key !== 'itemId')
        .map(key => ({
          field: { field: key },
          value: d.props[key]
        }));
      await dispatch(updateFields(dataToUpdate));
      await dispatch(updateDataRaw(d, false));
    } else {
      await dispatch(updateDataRaw(d, false));
    }
  };

const updateDataRaw =
  (d: Partial<Metadata>, replaceData?: boolean): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    if (!d.props?.refId) {
      if (!!getState().form?.data?.props?.refId) {
        throw new Error('RefId is required to update data');
      } else if (d.props) {
        dispatch(
          updateFields(
            Object.keys(d.props)
              .filter(key => key != 'refId')
              .map(key => ({
                field: { field: key },
                value: d.props[key]
              }))
          )
        );
        return;
      }
    }
    if (Object.keys(d.props).length == 1)
      throw new Error('No fields to update');
    const { planning } = getState();
    const { updateData } = getAPI();
    const schema = getSchema(planning.selectedWorkspace.schemas, d.labels);
    if (!schema) throw new Error('Invalid Schema for update');
    try {
      await updateData({
        data: d
      }).catch(e => {
        if (
          [
            ErrorName.FieldValidationError,
            ErrorName.DuplicateFieldError
          ].includes(e.name) &&
          e.additionalInfo
        ) {
          if (getState().form?.data?.props?.refId == d.props.refId) {
            dispatch(updateFieldErrors(e.additionalInfo));
          }
        }
        throw e;
      });
      if (!WebSocketManager.getInstance().isConnected()) {
        await dispatch(reloadData(d.props.refId, d.labels, replaceData));
      }
    } catch (err) {
      const latestDataRefId = getState().form?.data?.props?.refId;
      if (latestDataRefId == d.props.refId) {
        throw err;
      } else {
        console.error(err);
      }
    }
  };

export const reloadData =
  (
    refId: string,
    labels: string[],
    replaceData?: boolean
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    if (!refId) {
      throw new Error('RefId is required to reload data');
    }
    const { planning } = getState();
    const { getDataDetail } = getAPI();
    const schema = getSchema(planning.selectedWorkspace.schemas, labels);
    if (!schema) throw new Error('Invalid Schema for reload');
    try {
      schema.lastDataModifiedTime = new Date();
      const formData = getState().form?.data;
      if (formData && formData.props.refId == refId) {
        const reqNum = new Date().getTime();
        dispatch(updateMetadataFormState({ reqNum }));
        await getDataDetail(formData.props.refId, schema?.props?.type).then(
          ({ data: r }) => {
            if (r?.length && r[0]) {
              if (replaceData) {
                dispatch(updateMetadataFormData({ reqNum, data: r[0] }));
              } else {
                dispatch(updateMetadataFormState({ reqNum, original: r[0] }));
              }
            }
          }
        );
      }
      dispatch(reloadDataInView(refId));
    } catch (err) {
      const latestDataRefId = getState().form?.data?.props?.refId;
      if (latestDataRefId == refId) {
        throw err;
      } else {
        console.error(err);
      }
    }
  };

const setPendingRetirement = (
  data: Partial<Metadata>,
  retirementProps?: RetirementProps
) =>
  setStatus(
    data,
    {
      id: '20',
      labels: [NodeType.Status],
      props: {
        description: 'Pending Retirement',
        refId: 'status:20',
        id: 20
      }
    },
    retirementProps
  );

const setRetired = (data: Partial<Metadata>) =>
  setStatus(data, {
    id: '25',
    labels: [NodeType.Status],
    props: {
      description: 'Retired',
      refId: 'status:25',
      id: 25
    }
  });

const setStatus =
  (
    data: Partial<Metadata>,
    newStatus: Metadata,
    retirementProps?: RetirementProps
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { form } = getState();
    const { schema } = form;
    const { updateData, getDataDetail } = getAPI();
    await updateData({
      data: {
        labels: data.labels,
        props: {
          refId: data.props.refId,
          status: [newStatus],
          ...retirementProps
        }
      }
    });
    getDataDetail(data.props.refId, schema?.props?.type).then(r => {
      if (r?.data?.length && r.data[0]) {
        const d = r.data[0];
        const formData = getState().form?.data;
        if (d?.props?.refId == formData?.props?.refId) {
          dispatch(
            updateMetadataFormStateField({ data: d, original: clone(d) })
          );
        }
      }
    });
    dispatch(reloadDataInView(data?.props?.refId));
  };

const getViewDataToReload = async (
  curReqNum: number,
  getState: () => S,
  api: API,
  newDataRefId?: string
) => {
  const { view, planning, form } = getState();
  if (!form || !view) return;
  const { schema, data } = form;
  if (!view?.selected?.props?.type) return;
  const selView = view.selected;
  const { payload } = getSearchPayload(view, selView, planning);
  const { searchGroups } = api;
  const dataRefId = newDataRefId || data?.props?.refId;
  if (!dataRefId && data) return;
  if (selView.props.type != ViewType.Kanban) {
    return { selView, reqNum: curReqNum, pagination: view.pagination };
  } else if (schema) {
    const displayField = findOne(schema.props?.fields, {
      field: schema.props?.displayField
    });
    const payloadWithItem = clone(payload);
    if (!payloadWithItem.filters) payloadWithItem.filters = [];
    if (dataRefId) {
      payloadWithItem.filters.push({
        field: displayField.key,
        conditions: [{ [Op.refIds]: [dataRefId] }]
      });
    }
    const groupIdsToRefresh = [];
    if (dataRefId) {
      Object.entries(view?.groupData).forEach(([id, data]) => {
        if (data?.filter(d => d?.props?.refId == dataRefId).length > 0) {
          groupIdsToRefresh.push(id);
        }
      });
    }

    if (curReqNum < reqNum) return;

    if (view.selected.props.groupBy.length <= 1) {
      payload.includeCards = true;
      const allGroups = await searchGroups(payload);
      const { groups, data, info } = allGroups;
      return { selView, groups, newCards: data, reqNum: curReqNum, info };
    } else {
      const allGroups = await searchGroups(payload);
      if (curReqNum < reqNum) return;
      const itemGroups = await searchGroups(payloadWithItem);
      const groups = allGroups.groups;
      const swimlanes = allGroups.swimlanes;
      const info = allGroups.info;
      const curSwimlanes = getState().view?.swimlanes;
      swimlanes?.forEach(sw => {
        const s = findOne(curSwimlanes, { id: sw.id });
        if (s) {
          sw.subgroups = s.subgroups;
          if (typeof s.expanded == 'boolean') {
            sw.expanded = s.expanded;
            const hasCard = sw.subgroups?.reduce(
              (bool, subgroup) =>
                bool || subgroup?.cardRefIds?.includes(dataRefId),
              false
            );
            if (hasCard) {
              sw.subgroups = null;
              sw.prevSubGroups = s.subgroups;
            } else {
              const itemSwimline = findOne(itemGroups.swimlanes, { id: sw.id });
              if (itemSwimline?.count) {
                sw.subgroups = null;
                sw.prevSubGroups = s.subgroups;
              }
            }
          }
        }
      });
      return { selView, groups, swimlanes, reqNum: curReqNum, info };
    }
  }
};

let reqNum = 0;
const reloadDataInView =
  (updatedDataRefId?: string): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const d = await getViewDataToReload(
      ++reqNum,
      getState,
      getAPI(),
      updatedDataRefId
    );
    if (d && d.reqNum >= reqNum) {
      const { groups, swimlanes, selView, pagination, newCards, info } = d;
      if (groups) {
        dispatch(
          updateViewState(selView, { groups, swimlanes, newCards, info })
        );
      } else if (selView.props.type != ViewType.Kanban) {
        dispatch(
          loadData({
            forceReload: true,
            ignoreLoading: true,
            numOfResults: pagination?.npp
          })
        );
      }
    }
  };

const validateWorkspace =
  (): ElementAction<Functions, S> => (dispatch, getState) => {
    const { form, planning } = getState();
    const { data, schema } = form;
    const { selectedWorkspace } = planning;
    if (!data || !selectedWorkspace?.disableItemDataRefIds?.length) return;
    const schemaForm = getSchemaForm(schema, data);
    const invalidDataRef = getInvalidItemDataReference(
      selectedWorkspace,
      schemaForm,
      data
    );
    if (!!invalidDataRef) {
      throw new InvalidWorkspaceError(
        'Failed to update item',
        {
          invalidDataRef,
          itemRefId: data.props.refId
        },
        'warning'
      );
    }
  };

const doAction =
  (
    action: string,
    actionEmployeeId: number,
    backingUpFor: View.BackingUpFor,
    adminApproval: boolean
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    try {
      dispatch(updateApprovalState({ approvalInProgress: true }));
      const { form, view } = getState();
      const { data, schema } = form;
      const { updateData, getDataDetail } = getAPI();

      let pushToCPPNext = false;
      const approvals = data?.approvals || [];
      const isTaskActionable = (task: ApprovalTask) =>
        adminApproval || canActionTask(task, backingUpFor, actionEmployeeId);

      const taskActions = approvals
        .filter(task => isTaskActionable(task))
        .filter(task => task?.approvalStatus != action)
        .map(task => ({
          role: task.role,
          approvalStatus: action
        }));

      if (adminApproval) {
        pushToCPPNext = true;
      } else {
        pushToCPPNext = approvals.every(task => {
          const nextStatus = isTaskActionable(task)
            ? action
            : task?.approvalStatus;
          return nextStatus == ApprovalType.Approved;
        });
      }
      if (
        pushToCPPNext &&
        action == ApprovalType.Approved &&
        getItemStatusId(data) == ItemStatus.Concept_Review
      ) {
        await dispatch(pushToCPP(true));
        const { form } = getState();
        const { pushResponse } = form;
        if (pushResponse?.errors?.length > 0) return;
      }

      if (!taskActions.length) return;
      await updateData({
        data: {
          labels: data.labels,
          props: {
            refId: data.props.refId
          },
          taskActions,
          pushToCPPNext
        }
      });

      const s = schema || view.selected.dataSchema;
      if (data?.props?.refId) {
        const r = await getDataDetail(data.props.refId, s?.props?.type);
        const d = r?.data[0];
        if (d) {
          dispatch(
            updateMetadataFormState({
              data: d,
              original: JSON.parse(JSON.stringify(d))
            })
          );
          if (
            d.approvals?.every(
              approval => approval?.approvalStatus == ApprovalType.Approved
            )
          ) {
            if (getItemStatusId(d) == ItemStatus.Concept_Review) {
              dispatch(pushToCPP());
            } else if (getItemStatusId(d) == ItemStatus.PendingRetirement) {
              dispatch(setRetired(d));
            } else {
              dispatch(reloadDataInView(data.props.refId));
            }
          } else {
            dispatch(reloadDataInView(data.props.refId));
          }
        }
      }
    } catch (e) {
      throw e;
    } finally {
      dispatch(updateApprovalState({ approvalInProgress: false }));
    }
  };

const setAllErrorFields =
  (allErrorFields): ElementAction<Functions, S> =>
  async dispatch => {
    dispatch(updateAllErrorFields(allErrorFields));
  };

const setCollapsedAccordions =
  (collapsedAccordions: string[]): ElementAction<Functions, S> =>
  async dispatch => {
    dispatch(setUserSetting(AppRefId, { collapsedAccordions }));
  };

export const useActions = () => {
  const dispatch: ThunkDispatch<S, any, any> = useDispatch();
  return {
    validateWorkspace: () => dispatch(validateWorkspace()),
    showForm: async (
      data: string | Partial<Metadata>,
      s?: Schema,
      commentId?: string,
      m?: string
    ) => dispatch(showForm(data, s, commentId, m)),
    setShowFieldsOption: (showFieldsOption: ShowFieldsOption) =>
      dispatch(setUserSetting(AppRefId, { showFieldsOption })),
    updateField: async (f: SchemaField, v: any) => dispatch(updateField(f, v)),
    updateFields: async (fields: FieldData[]) => dispatch(updateFields(fields)),
    updateData: (f: SchemaField | FieldData[], v: any) =>
      dispatch(updateData(f, v)),
    updateDataAndReload: async (d: Partial<Metadata>, reload: boolean = true) =>
      dispatch(updateDataRaw(d, reload)),
    updateConfigureWorkflow: async (
      d: Partial<Metadata>,
      isLightUpdate: boolean,
      itemStatusId: number
    ) => dispatch(updateConfigureWorkflow(d, isLightUpdate, itemStatusId)),
    deleteData: async (d: { refId: string; type: NodeType }) =>
      await dispatch(deleteData(d.refId, d.type)),
    closeForm: () => dispatch(closeForm()),
    slideOut: () => dispatch(slideOut()),
    create: (
      tw: ToasterWithLinkWrapper,
      addAdditional?: boolean,
      createInContent?: boolean
    ) => dispatch(create(tw, addAdditional, createInContent)),
    createComment: (
      message: string,
      mentions: any[],
      parentCommentRefId: string
    ) => dispatch(createComment(message, mentions, parentCommentRefId)),
    updateComment: (refId: string, props: any) =>
      dispatch(updateComment(refId, props)),
    deleteComment: (refId: string) => dispatch(deleteComment(refId)),
    loadComments: (refId: string) => dispatch(loadComments(refId)),
    showHideCommentWidget: () => dispatch(showHideCommentWidget()),
    pushToCPP: () => dispatch(pushToCPP(false)),
    validate: () => dispatch(validate()),
    runRules: (data: Partial<Metadata>) => dispatch(runRules(data)),
    validateModalData: (modalData: Partial<Metadata>) =>
      dispatch(validateModalData(modalData)),
    getAutoAcceptImpliedDataProps: async (
      data: Partial<Metadata>,
      schema: Schema
    ) => dispatch(getAutoAcceptImpliedDataProps(data, schema)),
    updateShouldValidate: (shouldValidate: ValidationMode) =>
      dispatch(updateShouldValidate({ shouldValidate })),
    setPendingRetirement: (data: Partial<Metadata>, props: RetirementProps) =>
      dispatch(setPendingRetirement(data, props)),
    doAction: (
      action: string,
      actionEmployeeId: number,
      backingUpFor: View.BackingUpFor,
      adminApproval: boolean
    ) =>
      dispatch(doAction(action, actionEmployeeId, backingUpFor, adminApproval)),
    setAllErrorFields: allErrorFields =>
      dispatch(setAllErrorFields(allErrorFields)),
    setCollapsedAccordions: (collapsedAccordions: string[]) =>
      dispatch(setCollapsedAccordions(collapsedAccordions)),
    setShowConfigureWorkflowModal: (show: boolean) =>
      dispatch(
        updateMetadataFormStateField({ showConfigureWorkflowModal: show })
      ),
    reloadData: (refId: string, labels: string[], replaceData?: boolean) =>
      dispatch(reloadData(refId, labels, replaceData))
  };
};

const createSearchPayload = (
  planning: PlanningState,
  s: Schema,
  data: Partial<Metadata>,
  includeAll?: boolean
): any => ({
  workspaceRefId: planning.selectedWorkspace.props?.refId,
  schemas: planning.selectedWorkspace.schemas,
  includeImplied: includeAll,
  includeComputed: includeAll,
  filters: [
    {
      field: `${s.props.type}.${s.props.displayField}`,
      conditions: [{ [Op.refIds]: [data.props.refId] }]
    }
  ],
  view: {
    card: {
      props: {
        data: s.props.type
      }
    },
    dataSchema: {
      props: {
        workspaceId: s?.props?.workspaceId
      }
    }
  }
});

const getEmptyRequiredFields = (schema: Schema, data: Partial<Metadata>) => {
  return getSchemaForm(schema, data).props.fields.filter(
    f =>
      fieldIsRequired(f, data) &&
      isRequiredFieldValueEmpty(getFieldValue(f, data))
  );
};

const setDefaultValueForRequiredBooleans = (
  schema: Schema,
  data: Partial<Metadata>
) => {
  if (!data.props) {
    data.props = {};
  }
  return getSchemaForm(schema, data)
    .props.fields.filter(
      f => fieldIsRequired(f, data) && f.dataType == DataType.boolean
    )
    .map(field => {
      if (
        data?.props[field.field] !== true &&
        data?.props[field.field] !== false
      ) {
        data.props[field.field] = false;
      }
    });
};
